import LegalDocShell from "@/components/legal/LegalDocShell";
import { buildLegalSections } from "@/lib/legalRenderer";

const SECTIONS = buildLegalSections([
  { id: "principles", title: "1. Our Privacy Principles", body:
`NaturePulse is built around public participation and citizen observation, but participation should not require giving up unnecessary personal privacy.

Our guiding principles are:
- Collect information for legitimate purposes
- Avoid collecting information we do not reasonably need
- Clearly distinguish public contributions from private information
- Give users reasonable control over their information
- Protect precise location information where appropriate
- Be especially careful with sensitive wildlife and farm locations
- Use reasonable safeguards to protect information
- Explain how AI may process submitted information
- Be transparent about research and aggregated-data uses
- Respect applicable privacy rights`
  },
  { id: "info-collected", title: "2. Information We May Collect", body:
`The information we collect depends upon how you use NaturePulse.

## Account Information
When you create an account, we may collect information such as:
- Name
- Username/display name
- Email address
- Telephone number
- Country
- State/province
- City or general location
- Profile photograph
- Preferred language
- Account preferences
- Authentication information
- Communication preferences

We may not require all of this information for every user.`
  },
  { id: "observations", title: "3. Nature and Environmental Observations", body:
`When you contribute an observation, we may collect information such as:
- Photographs
- Videos
- Audio
- Observation title
- Description
- Date
- Time
- Location
- GPS coordinates
- Species
- Plant name
- Animal name
- Bird name
- Insect name
- Environmental conditions
- Weather conditions
- Habitat information
- Tags
- Categories
- Comments
- Identification suggestions
- Other information you voluntarily provide

Some of this information may be publicly displayed when you choose to make an observation public.`
  },
  { id: "farmers-pulse", title: "4. Farmers Pulse Information", body:
`Users participating in Farmers Pulse may provide additional agricultural information. This may include:
- Farm name
- General farm location
- Field or plot information
- Crop types
- Crop varieties
- Planting dates
- Harvest dates
- Crop photographs
- Soil observations
- Irrigation information
- Rainfall observations
- Water conditions
- Pest observations
- Plant disease observations
- Weed observations
- Pollinator observations
- Livestock information
- Poultry information
- Agricultural practices
- Harvest observations
- Yield information
- Farm notes
- Farm diary entries
- Agricultural questions
- Farmer-to-farmer discussions
- Market observations
- Other farming information

Not all Farmers Pulse information needs to be public. Where functionality permits, users may be provided with choices regarding whether particular information is Private, Shared, Community-visible, or Public.`
  },
  { id: "location", title: "5. Location and GPS Information", body:
`Location is an important component of NaturePulse because environmental and agricultural observations are often meaningful only when associated with a place.

With appropriate permissions, NaturePulse may collect or process:
- Country
- State/province
- District/county
- City
- Town
- Village
- General geographic area
- GPS coordinates
- Approximate device location
- Location associated with photographs
- Farm or field locations

## Precise Location
We recognize that precise geographic coordinates can be sensitive. A precise location could potentially reveal:
- A user's home
- A private farm
- A particular agricultural field
- A sensitive wildlife habitat
- An endangered species location
- A nesting site
- A valuable crop
- Private property
- Another sensitive location

NaturePulse may therefore provide tools to reduce, obscure, generalize, delay, or restrict precise geographic information. For example, a public observation might display the general village, city, county, or geographic region rather than the exact GPS coordinates.

We strongly encourage users to consider the privacy and safety implications before publicly sharing exact locations.`
  },
  { id: "photo-metadata", title: "6. Location Information Embedded in Photographs", body:
`Photographs taken with smartphones and digital cameras may contain metadata, including:
- Date
- Time
- Camera information
- GPS coordinates
- Device information

NaturePulse may process this metadata to assist with organizing observations, suggesting locations, generating maps, or creating titles and descriptions.

Where appropriate, NaturePulse may remove or obscure certain metadata from publicly displayed versions of photographs even when the underlying information is retained for the user's private records or platform functionality.`
  },
  { id: "public-info", title: "7. Public Information", body:
`NaturePulse is partly designed as a public observation and knowledge-sharing platform. Information you intentionally designate as public may be visible to anyone.

Public information may include:
- Display name
- Profile photograph
- Public observations
- Photographs
- Videos
- Comments
- Articles
- Questions and answers
- Public farmer observations
- General observation location
- Date and time
- Species identification
- Crop information
- Tags
- Community contributions

Public information may also be indexed by search engines or shared through links and social media. Do not include information in a public contribution that you wish to keep private.`
  },
  { id: "private-info", title: "8. Private Information", body:
`Information designated private will not ordinarily be publicly displayed. Private information may include, depending upon available features:
- Personal farm records
- Private observations
- Farm diaries
- Private notes
- Precise farm locations
- Private field information
- Draft posts
- Personal calendars
- Account information
- Notification settings

Authorized personnel and service providers may access private information when reasonably necessary to operate, maintain, secure, troubleshoot, or support the Services.`
  },
  { id: "how-we-use", title: "9. How We Use Information", body:
`NaturePulse may use collected information to:
- Provide and operate NaturePulse
- Provide Farmers Pulse
- Maintain user accounts
- Store observations
- Organize photographs
- Create geographic maps
- Display environmental observations
- Display agricultural observations
- Identify plants and wildlife
- Assist with crop identification
- Assist with pest and disease identification
- Generate suggested descriptions
- Generate tags and categories
- Provide search functionality
- Personalize relevant content
- Provide weather-related information
- Generate agricultural calendars
- Provide notifications and alerts
- Facilitate community discussions
- Connect related observations
- Detect spam and abuse
- Protect users and the platform
- Improve our Services
- Analyze environmental patterns
- Analyze agricultural patterns
- Generate aggregated statistics
- Support citizen science
- Support research
- Develop new NaturePulse features
- Comply with applicable legal requirements`
  },
  { id: "ai", title: "10. Artificial Intelligence", body:
`NaturePulse may use AI and machine-learning systems to help process information. For example, AI may assist with:
- Identifying plants
- Identifying animals
- Identifying birds
- Identifying insects
- Identifying crops
- Suggesting possible pests or diseases
- Analyzing photographs
- Reading photograph metadata
- Creating titles
- Generating descriptions
- Suggesting tags
- Translating content
- Summarizing information
- Categorizing observations
- Answering questions
- Detecting potentially inappropriate content
- Finding relationships among observations

Information submitted to an AI-enabled feature may be processed by NaturePulse systems or by technology providers working on our behalf. Where third-party AI providers process information for NaturePulse, we seek to use providers under appropriate contractual, privacy, and security arrangements.

Users should avoid submitting unnecessary sensitive personal information to AI features. See the [AI Use & Agricultural Disclaimer Policy](/legal/ai) for further detail.`
  },
  { id: "research", title: "11. Citizen Science and Research", body:
`One of NaturePulse's goals is to create a valuable long-term record of our changing natural world.

NaturePulse observations may contribute to citizen-science and research initiatives concerning subjects such as:
- Biodiversity
- Climate change
- Agriculture
- Crop patterns
- Planting and harvesting seasons
- Pest distribution
- Plant diseases
- Pollinators
- Wildlife distribution
- Flowering patterns
- Migration
- Rainfall
- Drought
- Flooding
- Soil conditions
- Water conditions
- Habitat changes
- Environmental trends

NaturePulse may create aggregated, statistical, anonymized, de-identified, or otherwise appropriately processed datasets from user observations. Such information may be used internally or, where appropriate, shared with:
- Researchers
- Universities
- Schools
- Agricultural institutions
- Government agencies
- Environmental organizations
- Conservation organizations
- Nonprofit organizations
- Other research or public-interest organizations

We will seek to avoid unnecessarily identifying individual users when aggregated or de-identified information is sufficient for the intended purpose. Additional detail lives in the [Research & Data Use Policy](/legal/research).`
  },
  { id: "photos-content", title: "12. Your Photographs and Content", body:
`You retain ownership of original photographs and other original content you contribute, subject to the license provided in our [Terms of Service](/legal/terms).

NaturePulse may process your content to operate the Services, including to:
- Store it
- Display it
- Resize it
- Create thumbnails
- Categorize it
- Analyze it
- Generate metadata
- Place observations on maps
- Create geographic collections
- Associate related observations
- Make public content searchable
- Include appropriate observations in aggregated research or educational resources

Please review the [User-Generated Content Policy](/legal/user-content) for additional detail regarding content ownership and licenses.`
  },
  { id: "sharing", title: "13. Information Sharing", body:
`We may share information in limited circumstances.

## With Your Direction
We may share information when you intentionally publish, post, send, or otherwise direct us to share it.

## Service Providers
We may use third-party companies to provide services such as:
- Website hosting
- Cloud storage
- Database hosting
- Email
- Authentication
- Mapping
- Analytics
- AI processing
- Image processing
- Security
- Notifications
- Customer support

These providers may process information necessary to perform services for us.

## Research and Public-Interest Organizations
We may provide appropriately aggregated, anonymized, de-identified, public, or otherwise authorized information for legitimate research, education, conservation, agricultural, environmental, or public-interest purposes.

## Legal Requirements
We may disclose information when reasonably necessary to comply with applicable law, court orders, legal processes, or valid governmental requests.

## Safety and Security
We may disclose information when reasonably necessary to investigate fraud, abuse, security threats, illegal activities, or credible threats to people or the Services.`
  },
  { id: "no-sale", title: "14. Sale of Personal Information", body:
`NaturePulse does not intend to build its mission around selling users' personal information.

If our practices change in a way that constitutes the "sale" or "sharing" of personal information under applicable privacy law, we will provide legally required disclosures and choices.

Where required by applicable law, eligible users may have the right to opt out of the sale or sharing of personal information.`
  },
  { id: "cookies", title: "15. Cookies and Similar Technologies", body:
`NaturePulse may use cookies, local storage, pixels, SDKs, and similar technologies. These technologies may be used for:
- Authentication
- Remembering preferences
- Security
- Session management
- Analytics
- Performance
- Feature functionality
- Preventing abuse
- Understanding how the Services are used

Where legally required, users may be provided choices concerning non-essential cookies. Your browser may also allow you to control or delete cookies. See the [Cookie Policy](/legal/cookies) for a full breakdown.`
  },
  { id: "analytics", title: "16. Analytics", body:
`NaturePulse may use analytics services to understand how visitors use the platform. Analytics information may include:
- Pages visited
- Features used
- Approximate geographic region
- Browser type
- Device type
- Operating system
- Referring page
- Date and time
- Session information
- IP address or related technical identifiers

We use this information to improve performance, usability, security, and features.`
  },
  { id: "communications", title: "17. Communications and Notifications", body:
`If you provide contact information, NaturePulse may send communications relating to:
- Account verification
- Security
- Observations
- Comments
- Community activity
- Weather information
- Agricultural alerts
- Farmers Pulse updates
- Research participation
- Product changes
- Privacy or Terms updates
- Other requested notifications

You may be able to manage non-essential communications through your account settings or unsubscribe mechanisms. Certain security, account, transactional, or legally required communications may continue even if you opt out of promotional messages.`
  },
  { id: "children", title: "18. Children's Privacy", body:
`NaturePulse may contain educational material useful to students and families. However, children require additional privacy protections.

NaturePulse is not intended to knowingly collect personal information from children in violation of applicable law. Where parental or guardian consent is legally required, appropriate consent should be obtained before collecting personal information from a child.

We may introduce special educational or student features with additional privacy protections — see the [Youth Privacy Policy](/about/youth-privacy).

Parents or guardians who believe a child has submitted personal information improperly should contact us so that we can review the situation and take appropriate action.`
  },
  { id: "sensitive-wildlife", title: "19. Sensitive Wildlife Locations", body:
`NaturePulse recognizes that publishing exact locations of endangered, threatened, rare, nesting, breeding, or commercially valuable species can cause harm.

We may obscure, generalize, delay, or remove geographic information associated with sensitive species. NaturePulse may also restrict access to such information even when the contributor originally submitted precise coordinates.

Protecting nature may sometimes require protecting its location.`
  },
  { id: "farm-privacy", title: "20. Farm Privacy and Agricultural Security", body:
`Farmers Pulse may contain information that could be sensitive to farmers. Examples include:
- Exact farm coordinates
- Field boundaries
- Crop locations
- Livestock locations
- Production information
- Yield information
- Water sources
- Pest outbreaks
- Disease information
- Private farm notes

Farmers should carefully select what information they make public. NaturePulse may provide privacy controls allowing farmers to publish general regional information without revealing exact farm locations.

We encourage farmers to keep commercially sensitive or security-sensitive information private unless they intentionally wish to share it.`
  },
  { id: "retention", title: "21. Data Retention", body:
`We retain personal information for as long as reasonably necessary for the purposes described in this Privacy Policy, including:
- Providing the Services
- Maintaining accounts
- Preserving requested observations
- Maintaining historical environmental records
- Complying with legal obligations
- Resolving disputes
- Preventing abuse
- Protecting the security of NaturePulse

Different categories of information may have different retention periods. Public environmental observations may have long-term historical or scientific value.

If an account is deleted, certain information may remain where legally permitted in:
- Backups
- Security logs
- Aggregated datasets
- Anonymized datasets
- Historical records
- Research datasets where removal is impractical or would compromise previously completed research

Where appropriate, personally identifying information may be removed while preserving the underlying environmental observation.`
  },
  { id: "security", title: "22. Data Security", body:
`NaturePulse uses reasonable administrative, technical, and organizational safeguards designed to protect personal information. These may include, where appropriate:
- Encryption in transit
- Secure authentication
- Access controls
- Restricted administrative access
- Database security
- Security monitoring
- Backups
- Software updates
- Service-provider security requirements
- Other reasonable safeguards

No internet service can guarantee absolute security. Users should also protect their accounts, devices, email accounts, and authentication methods.`
  },
  { id: "your-choices", title: "23. Your Privacy Choices", body:
`Depending upon your location and applicable law, you may have rights concerning your personal information. These may include the right to:
- Know what information we collect
- Access personal information
- Request correction
- Request deletion
- Obtain information about how data is used
- Obtain information about certain disclosures
- Opt out of certain processing
- Opt out of certain sale or sharing where applicable
- Limit certain uses of sensitive personal information where applicable
- Withdraw consent where processing depends upon consent
- Appeal certain privacy decisions where applicable

Not every right applies in every jurisdiction or circumstance. We may need to verify your identity before fulfilling certain requests.`
  },
  { id: "california", title: "24. California Privacy Rights", body:
`California residents may have additional rights under applicable California privacy laws.

Depending upon whether NaturePulse/Social Pulse Inc. is subject to a particular requirement and whether an exception applies, these rights may include:
- The right to know
- The right to access
- The right to delete
- The right to correct
- The right to opt out of qualifying sale or sharing
- The right to limit certain uses and disclosures of sensitive personal information
- The right not to receive discriminatory treatment for exercising applicable privacy rights

California users may contact us using the privacy contact information below.`
  },
  { id: "international", title: "25. International Users", body:
`NaturePulse is designed with a worldwide community in mind. Privacy laws differ among countries.

Users in certain jurisdictions may have additional rights concerning access, correction, deletion, portability, objection, restriction of processing, or withdrawal of consent.

Where applicable privacy law provides additional rights, NaturePulse will seek to honor those rights.`
  },
  { id: "third-party", title: "26. Third-Party Websites", body:
`NaturePulse may contain links to:
- Government websites
- Universities
- Research organizations
- News publishers
- Agricultural resources
- Social media platforms
- Videos
- Weather services
- Mapping services
- Businesses
- Other websites

NaturePulse does not control the privacy practices of independent third-party websites. When you leave NaturePulse, the third party's privacy policy applies.`
  },
  { id: "business-transfers", title: "27. Business Transfers", body:
`If Social Pulse Inc. or NaturePulse is involved in a merger, acquisition, restructuring, financing, sale of assets, or similar business transaction, information may be transferred as part of that transaction where legally permitted.

Any successor organization receiving personal information will be required to handle it in accordance with applicable law and relevant privacy commitments.`
  },
  { id: "changes", title: "28. Changes to This Privacy Policy", body:
`NaturePulse will continue to evolve. We may update this Privacy Policy when our features, technologies, legal obligations, or information practices change.

When significant changes are made, we may provide notice through:
- NaturePulse.net
- The user's account
- Email
- An in-app notification
- Another appropriate method

The Last Updated date at the beginning of this Policy identifies the latest version.`
  },
  { id: "requests", title: "29. Privacy Requests", body:
`Users may contact NaturePulse to request access, correction, deletion, or other applicable privacy rights.

Privacy requests may be submitted to: legal@naturepulse.net

We may request information reasonably necessary to verify your identity and protect your account from unauthorized requests. Where required by law, we will respond within the applicable legal timeframe.`
  },
  { id: "contact", title: "30. Contact Information", body:
`Questions regarding this Privacy Policy or NaturePulse's privacy practices may be directed to:

NaturePulse.net, a division of Social Pulse Inc.
Website: [NaturePulse.net](https://naturepulse.net)
Privacy Email: legal@naturepulse.net
Support Email: support@naturepulse.net

Mailing Address:
Social Pulse Inc.
15166 SE Aston Loop
Portland, OR 97236
United States`
  },
]);

export default function PrivacyPolicy() {
  return (
    <LegalDocShell
      eyebrow="NaturePulse · Privacy Policy"
      title="Privacy Policy"
      effective="August 23, 2026"
      updated="August 23, 2026"
      intro={
        <>
          <p>NaturePulse.net, a division of Social Pulse Inc. ("NaturePulse," "we," "us," or "our") respects your privacy and is committed to protecting the personal information entrusted to us.</p>
          <p>NaturePulse is a platform for observing, documenting, learning about, and sharing information concerning nature, agriculture, biodiversity, weather, climate, wildlife, plants, habitats, and environmental change. NaturePulse includes specialized features and services such as Farmers Pulse, nature observations, photographs, geographic observations, community discussions, blogs, maps, alerts, research resources, artificial intelligence ("AI") tools, and other services that may be introduced over time.</p>
          <p>This Privacy Policy explains what information we may collect, how we use it, when it may be shared, the choices available to you, and how we work to protect it. By using NaturePulse.net or related NaturePulse services that reference this Privacy Policy, you acknowledge the practices described in this Policy.</p>
        </>
      }
      sections={SECTIONS}
    />
  );
}
